import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, from, map, of } from 'rxjs';
import { AuthSession, Profile, UserRole } from '../models/profile';
import {
  fetchAuthSession,
  signInWithRedirect,
  signOut,
} from 'aws-amplify/auth';
import { environment } from '../../../../environments/environment';
import { UserData } from '../../../shared/models/user-data.model';

const AUTHLOCALSTORAGETOKEN = `azUser`;
const DBUSERDATALOCALSTORAGETOKEN = `dbUserData`;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  currentUser$: Observable<any>;
  currentUserSubject: BehaviorSubject<any>;
  profileSubject: BehaviorSubject<any>;
  private _dbUserData: UserData | undefined;

  get dbUserData(): UserData | undefined {
    return this._dbUserData;
  }

  set dbUserData(value: UserData | undefined) {
    this._dbUserData = value;
    this.setDbUserDataToLocalStorage(value);
  }

  get currentUserValue(): any {
    return this.currentUserSubject.value;
  }

  constructor() {
    this.currentUserSubject = new BehaviorSubject<any>(undefined);
    this.profileSubject = new BehaviorSubject<any>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this._dbUserData = this.getDbUserDataFromLocalStorage();
  }

  loginSSO(provider: string): Observable<any> {
    return this.amplifyLoginSSO(provider).pipe(
      map((loginResult: any) => {
        return loginResult;
      }),
      catchError((err) => {
        console.log('Error logging in', err);
        throw new Error(err.message);
      }),
    );
  }

  logout() {
    this.amplifyLogout();
    localStorage.removeItem(AUTHLOCALSTORAGETOKEN);
    localStorage.removeItem(DBUSERDATALOCALSTORAGETOKEN);
    this._dbUserData = undefined;
    this.currentUserSubject.next(null);
  }

  getUserBySession(): Observable<AuthSession | undefined> {
    return this.amplifyGetUserSession().pipe(
      map((user: any) => {
        if (user) {
          const result = this.getUserAuthDataMap(user);
          this.setAuthFromLocalStorage(result);
          this.currentUserSubject.next(result);
          return result;
        } else {
          this.logout();
        }
      }),
      catchError(() => {
        return of(undefined);
      }),
    );
  }

  getAuthFromLocalStorage(): AuthSession | undefined {
    try {
      const lsValue = localStorage.getItem(AUTHLOCALSTORAGETOKEN);
      if (!lsValue) {
        return undefined;
      }
      const authData = JSON.parse(lsValue);
      return authData;
    } catch (error) {
      return undefined;
    }
  }

  setAuthFromLocalStorage(auth: AuthSession | undefined): boolean {
    if (auth && auth.session) {
      localStorage.setItem(AUTHLOCALSTORAGETOKEN, JSON.stringify(auth));
      return true;
    }
    return false;
  }

  private getDbUserDataFromLocalStorage(): UserData | undefined {
    try {
      const lsValue = localStorage.getItem(DBUSERDATALOCALSTORAGETOKEN);
      if (!lsValue) {
        return undefined;
      }
      return JSON.parse(lsValue);
    } catch (error) {
      return undefined;
    }
  }

  private setDbUserDataToLocalStorage(userData: UserData | undefined): void {
    if (userData) {
      localStorage.setItem(
        DBUSERDATALOCALSTORAGETOKEN,
        JSON.stringify(userData),
      );
    } else {
      localStorage.removeItem(DBUSERDATALOCALSTORAGETOKEN);
    }
  }

  private getUserAuthDataMap(loginResult: any): AuthSession {
    const result: AuthSession = {
      userId: loginResult.userSub,
      session: loginResult.tokens,
      payload: {
        sub: loginResult.tokens.idToken.payload.sub,
        firstname: loginResult.tokens.idToken.payload.given_name,
        lastname: loginResult.tokens.idToken.payload.middle_name,
        email: loginResult.tokens.idToken.payload.email,
        prid: loginResult.tokens.idToken.payload['preferred_username'].split(
          '@',
        )[0],
        email_verified: loginResult.tokens.idToken.payload.email_verified,
        groups: loginResult.tokens.idToken.payload['cognito:groups']
          ? loginResult.tokens.idToken.payload['cognito:groups']
          : '',
        adgroups: loginResult.tokens.idToken.payload['custom:adgroups']
          ? loginResult.tokens.idToken.payload['custom:adgroups']
          : '',
        exp: loginResult.tokens.idToken.payload.exp
          ? loginResult.tokens.idToken.payload.exp
          : 0,
      },
    };
    return result;
  }

  getProfile() {
    if (!this.currentUserValue) {
      return false;
    }

    const payload = this.currentUserValue.session.idToken.payload;

    const { email, middle_name, given_name } = payload;
    let groups = payload['custom:adgroups'];
    if (groups) {
      groups =
        groups[0] === '['
          ? groups.substring(1, groups.length - 1).split(', ')
          : groups.split(' ');
    } else {
      groups = [];
    }

    const prid = payload['preferred_username'].split('@')[0];

    const profile: Profile = {
      prid: prid,
      email: email,
      userPrincipalName: given_name,
      familyName: middle_name,
      givenName: given_name,
      groups,
      role: this.parseUserRoleFromGroups(groups),
      lastUpdate: new Date().toDateString(),
    };

    this.profileSubject.next(profile);

    return profile;
  }

  parseUserRoleFromGroups(groups: string[]): UserRole | null {
    if (groups.includes(environment.groups.admin)) {
      return UserRole.ADMIN;
    } else if (groups.includes(environment.groups.lead)) {
      return UserRole.LEADERSHIP_USER;
    } else if (groups.includes(environment.groups.user)) {
      return UserRole.TA_USER;
    }

    return null;
  }

  // http auth service
  amplifyLoginSSO(provider: string): Observable<any> {
    try {
      const authResult = signInWithRedirect({
        provider: {
          custom: provider,
        },
      });
      return from(authResult);
    } catch (error) {
      throw new Error('AZ-AUTH-LOGIN-ERROR: ' + error);
    }
  }

  /**
   * Logs the user in with the username and password
   * @param email
   * @param password
   */
  amplifyLogout(): Observable<any> {
    try {
      const logoutResult = signOut();
      return from(logoutResult);
    } catch (error) {
      throw new Error('AZ-AUTH-LOGOUT-ERROR: ' + error);
    }
  }

  amplifyGetUserSession(): Observable<any> {
    try {
      const authResult = fetchAuthSession();
      return from(authResult);
    } catch (error) {
      throw new Error('AZ-AUTH-GETUSER-ERROR: ' + error);
    }
  }
}
